import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn, DataTableFilterConfig } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ApiError, apiFetch } from "@/lib/api";
import type { EquipmentResource, Page, PropertyListItemResource, ReferenceListItemResource } from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { EQUIPMENT_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { useIsPhone } from "@/lib/use-media-query";

import { AddEquipmentDialog } from "./equipment/AddEquipmentDialog";
import { BulkEditEquipmentDialog } from "./equipment/BulkEditEquipmentDialog";

//: Mirrors `equipment_repo.EQUIPMENT_STATUS_REPLACED`/`_REMOVED` — this
//: screen's own selection mechanism (Story 12.6, R-E10) never offers a
//: checkbox for a row in either state, the identical exclusion the server
//: itself re-enforces (VC-12.6-09).
const RETIRED_STATUSES = new Set(["replaced", "removed"]);

/**
 * `/equipment` (Story 12.1, spec judgement call 4) — the existing SPA nav
 * destination (`frontend/src/lib/destinations.ts`), previously a
 * `DestinationPlaceholder`, now the real portfolio-wide equipment list.
 * Mirrors `DocumentsList.tsx`'s own shared-`DataTable` shape (property/
 * category filters, no saved-view chips needed here — this story names no
 * saved-view requirement) plus `TenantsList.tsx`'s own "Add" button
 * placement (spec judgement call 2's second entry point — `PropertyRecord.
 * tsx`'s own "Add equipment" action, property pre-selected, is the first).
 *
 * `property_id` chosen explicitly here (never pre-filled) — this screen has
 * no one property in view, unlike `PropertyRecord.tsx`'s own capture entry
 * point.
 */

const PROPERTY_FILTER_FIELD = "property_id";
const CATEGORY_FILTER_FIELD = "category";
const FILTERABLE_FIELDS = [PROPERTY_FILTER_FIELD, CATEGORY_FILTER_FIELD] as const;

export function Equipment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const isReadOnly = useIsReadOnly();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  useEffect(() => {
    document.title = "Equipment — PropDesk";
  }, []);

  const propertiesQuery = useQuery({
    queryKey: ["properties", "list", "for-equipment-filter"],
    queryFn: () => apiFetch<Page<PropertyListItemResource>>("/properties?page_size=200"),
  });
  const categoriesQuery = useQuery({
    queryKey: ["reference-list-items", "equipment_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=equipment_category"),
  });
  const categoryLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of categoriesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [categoriesQuery.data]);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["equipment", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<EquipmentResource>>(`/equipment?${search}`),
    placeholderData: keepPreviousData,
  });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["equipment", "total"],
    queryFn: () => apiFetch<Page<EquipmentResource>>("/equipment?page_size=1"),
    enabled: hasActiveFilters,
  });

  const filters: DataTableFilterConfig[] = [
    {
      field: PROPERTY_FILTER_FIELD,
      label: "Property",
      options: (propertiesQuery.data?.data ?? []).map((item) => ({ value: item.id, label: item.nickname })),
    },
    {
      field: CATEGORY_FILTER_FIELD,
      label: labelFor("category"),
      options: (categoriesQuery.data?.data ?? []).map((item) => ({ value: item.value, label: item.label })),
    },
  ];

  const columns: DataTableColumn<EquipmentResource>[] = [
    {
      key: "category",
      render: (row) => categoryLabelByValue[row.category] ?? row.category,
    },
    {
      key: "make",
      sortable: false,
      render: (row) => (row.make && row.model_number ? `${row.make} ${row.model_number}` : (row.make ?? row.model_number ?? "—")),
    },
    { key: "serial_number", sortable: false, render: (row) => row.serial_number ?? "—" },
    { key: "property_nickname", sortable: false },
    { key: "unit_label", sortable: false },
    {
      key: "status",
      render: (row) => EQUIPMENT_STATUS_LABELS[row.status] ?? row.status,
    },
  ];

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Equipment</h1>
          <p className="mt-2 text-body text-ink-secondary">Every piece of equipment across the whole portfolio.</p>
        </div>
        {!isReadOnly ? (
          <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
            Add equipment
          </Button>
        ) : null}
      </div>
      {/*
       * The selection-count toolbar (Story 12.6, spec judgement call 4) —
       * appears only once at least one row is selected, offering exactly
       * the bulk-editable field list via `BulkEditEquipmentDialog` below.
       */}
      {!isReadOnly && selectedIds.size > 0 ? (
        <div className="mb-2 flex items-center justify-between rounded-lg border border-border-hairline bg-surface px-4 py-2">
          <p className="text-body text-ink">
            {selectedIds.size} selected
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
            <Button type="button" onClick={() => setBulkEditOpen(true)}>
              Bulk edit…
            </Button>
          </div>
        </div>
      ) : null}
      <DataTable
        caption="Equipment"
        itemNamePlural="equipment records"
        tint="amber"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        filters={filters}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        getRowHref={(row) => `/equipment/${row.id}`}
        {...(!isReadOnly
          ? {
              selection: {
                selectedIds,
                onSelectedIdsChange: setSelectedIds,
                isRowSelectable: (row: EquipmentResource) => !RETIRED_STATUSES.has(row.status),
              },
            }
          : {})}
        emptyState={
          !isReadOnly
            ? {
                heading: "No equipment yet.",
                body: "Log the first piece of equipment — photo the data plate, pick a category, type the serial, save.",
                action: { label: "Add equipment", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No equipment yet.",
                body: "Equipment will appear here once an Owner or Member adds one.",
              }
        }
      />
      {!isReadOnly ? (
        <AddEquipmentDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/equipment/${id}`)}
        />
      ) : null}
      {!isReadOnly ? (
        <BulkEditEquipmentDialog
          open={bulkEditOpen}
          onOpenChange={(next) => {
            setBulkEditOpen(next);
            if (!next) {
              // The dialog's own "Done" close is also the moment the
              // selection itself should clear — a stale selection sitting
              // around after a completed bulk edit would otherwise look
              // like it applied to nothing (module doc comment on
              // `RETIRED_STATUSES`'s own "matches server-side" reasoning).
              setSelectedIds(new Set());
            }
          }}
          selectedIds={[...selectedIds]}
          onApplied={() => {
            void queryClient.invalidateQueries({ queryKey: ["equipment", "list"] });
          }}
        />
      ) : null}
    </div>
  );
}
